// src/components/messaging/MessagingCenter.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ConversationList from './ConversationList';
import UserSearch from './UserSearch';


const MessagingCenter = () => {
  const { 
    conversations, 
    loading, 
    error, 
    refreshConversations,
    startConversation
  } = useMessaging();
  
  const { currentUser } = useAuth();
  console.log('MessagingCenter mounted. currentUser:', currentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [localError, setLocalError] = useState('');
  const initiatedChatRef = useRef(false);

  // Handle initiateChat from navigation state with enhanced validation
  useEffect(() => {
    const initiateChat = async () => {
      if (
        location.state &&
        location.state.initiateChat &&
        location.state.receiverId &&
        !initiatedChatRef.current &&
        currentUser
      ) {
        const receiverId = location.state.receiverId;
        
        // Validate receiverId is not current user
        if (receiverId === currentUser.uid) {
          console.error('MessagingCenter: Attempt to initiate chat with self detected via route', {
            currentUser: currentUser.uid,
            receiverId
          });
          setLocalError('Cannot start a conversation with yourself');
          
          // Clear the state to avoid repeated triggers
          navigate(location.pathname, { replace: true, state: {} });
          return;
        }
        
        // Mark as initiated to prevent duplicate attempts
        initiatedChatRef.current = true;
        
        try {
          const conversation = await startConversation(receiverId);
          // Navigate to the new conversation
          if (conversation?.id) {
            navigate(`/messages/${conversation.id}`, { replace: true });
          }
          // Clear the state after successful initiation
          navigate(location.pathname, { replace: true, state: {} });
        } catch (error) {
          console.error('Error initiating chat from route:', error);
          setLocalError(`Failed to start conversation: ${error.message}`);
          // Still clear state to prevent repeated errors
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    };
    
    initiateChat();
  }, [location, startConversation, navigate, currentUser]);
  
  // Handle user selection from search with enhanced validation
  const handleUserSelect = async (userId) => {
    setLocalError(''); // Clear any previous errors
    
    console.log('handleUserSelect called with userId:', userId, 'currentUser:', currentUser?.uid);
    
    if (!userId) {
      setLocalError('Invalid user selected');
      return;
    }
    
    if (!currentUser || !currentUser.uid) {
      setLocalError('You must be logged in to start a conversation');
      return;
    }
    
    // Double-check to prevent self-messaging
    if (userId === currentUser.uid) {
      console.error('MessagingCenter: Attempt to start conversation with self detected', {
        userId,
        currentUserId: currentUser.uid
      });
      setLocalError('You cannot start a conversation with yourself');
      return;
    }
    
    try {
      // Start or get conversation with selected user
      const conversation = await startConversation(userId);
      // Navigate to the conversation page
      if (conversation?.id) {
        navigate(`/messages/${conversation.id}`);
      }
      // Hide search after selection
      setShowUserSearch(false);
    } catch (error) {
      console.error('Error starting conversation:', error);
      setLocalError(`Failed to start conversation: ${error.message}`);
    }
};
  
  return (
    <div className="messaging-center">
      <h1>Messages</h1>
      
      {/* Display either context error or local error */}
      {(error || localError) && (
        <div className="error-message">{error || localError}</div>
      )}
      
      <div className="messaging-container">
        {/* Conversations list with search */}
        <div className="conversations-section">
          <div className="section-header">
            <h2>Conversations</h2>
            <button 
              className="new-message-button"
              onClick={() => setShowUserSearch(!showUserSearch)}
            >
              {showUserSearch ? 'Cancel' : 'New Message'}
            </button>
          </div>
          
          {showUserSearch ? (
            <UserSearch onUserSelect={handleUserSelect} />
          ) : (
            <ConversationList 
              conversations={conversations}
              onRefresh={refreshConversations}
              loading={loading}
              currentUserId={currentUser?.uid}
            />
          )}
        </div>
        
        {/* Instructions for users */}
        <div className="messaging-instructions">
          <div className="instruction-content">
            <h3>💬 Welcome to Messages</h3>
            <p>Click on any conversation to start chatting, or create a new message to begin a conversation.</p>
            <div className="instruction-features">
              <div className="feature-item">
                <span className="feature-icon">📝</span>
                <span>Click "New Message" to start a conversation</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💭</span>
                <span>Click any conversation to view full chat history</span>
</div>
              <div className="feature-item">
                <span className="feature-icon">🔄</span>
                <span>Use "Refresh" to check for new messages</span>
</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingCenter;